import { useMemo } from 'react'
import { Link, Navigate, useLocation } from 'react-router-dom'
import { LockKeyhole, Mail } from 'lucide-react'
import { PageMetadata } from '@/components/common/PageMetadata'
import { useAuthStore } from '@/stores/useAuthStore'
import { SetPasswordForm } from '@/features/auth/components/SetPasswordForm'
import { brandAssets } from '@/config/brandAssets'

export function SetPasswordPage(): React.ReactElement {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const location = useLocation()

  const token = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const value = params.get('token')?.trim()
    return value ? value : null
  }, [location.search])

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <div className="flex min-h-dvh min-w-0 flex-col overflow-x-hidden bg-white pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] lg:flex-row lg:flex-nowrap">
      <PageMetadata />
      <section className="relative order-2 flex w-full min-w-0 flex-col justify-between overflow-hidden bg-linear-to-br from-primary to-primary/90 px-4 py-8 text-white sm:px-6 sm:py-10 md:px-10 lg:order-1 lg:w-1/2 lg:px-12 lg:py-16">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.22),transparent_42%),radial-gradient(circle_at_bottom_right,rgba(17,24,39,0.16),transparent_38%)]" />
        <div className="relative z-10 mx-auto flex w-full max-w-4xl flex-1 flex-col justify-between">
          <div>
            <img src={brandAssets.logo} alt="KAAMCO" className="h-auto w-44 object-contain sm:w-56" />
            <p className="mt-8 max-w-xl text-3xl font-semibold leading-tight sm:text-4xl">
              Secure your account and choose a password that only you know.
            </p>
            <p className="mt-4 max-w-lg text-sm text-white/85 sm:text-base">
              This page works for both new account invitations and admin-initiated password resets.
            </p>
          </div>

          <div className="relative mt-10 grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-white/20 bg-white/12 p-4 backdrop-blur-md">
              <LockKeyhole className="h-5 w-5" aria-hidden />
              <p className="mt-3 text-sm font-semibold">One secure flow</p>
              <p className="mt-1 text-xs text-white/80">
                Invite and reset links both land here, so users see one clear password setup step.
              </p>
            </div>
            <div className="rounded-2xl border border-white/20 bg-white/12 p-4 backdrop-blur-md">
              <Mail className="h-5 w-5" aria-hidden />
              <p className="mt-3 text-sm font-semibold">Email-based access</p>
              <p className="mt-1 text-xs text-white/80">
                If this link has expired, request a new reset link or ask your administrator to send a fresh email.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="order-1 flex w-full min-w-0 flex-col items-center justify-center bg-white px-4 py-8 sm:px-6 sm:py-10 lg:order-2 lg:w-1/2 lg:px-8 lg:py-16">
        <div className="w-full max-w-md">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">
            Account setup
          </p>
          <SetPasswordForm token={token} />
          <p className="mt-4 text-center text-sm text-gray-500">
            Already signed in before?{' '}
            <Link to="/login" className="font-medium text-primary hover:underline">
              Go to login
            </Link>
            .
          </p>
        </div>
      </section>
    </div>
  )
}
